import { useEffect, useRef } from 'react'
import { FormFloatingFooter } from '@Components/claim-flow/form-floating-footer'
import { Button } from '@Components/button/button'
import { StepConfig } from '../../types'
import { formatIsoDateMMDDYYYY } from '../../format-utils'
import PreviousChevron from '@Assets/icons/previous-chevron.svg'

import './review-step.scss'

const CLASS_NAME = 'review-step'

type ReviewStepProps = {
  formData: Record<string, unknown>
  steps: StepConfig[]
  onSubmit: () => void
  onBack: () => void
  onEditStep: (index: number) => void
  isSubmitting: boolean
  error: string | null
}

function maskSSN(ssn: string): string {
  if (!ssn || ssn.length < 4) {
    return ssn
  }
  return `***-**-${ssn.slice(-4)}`
}

const EditIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
)

type FieldPair = {
  label: string
  value: string | undefined
}

const ReviewRow = ({ left, right }: { left?: FieldPair; right?: FieldPair }) => (
  <section className={`${CLASS_NAME}__row`}>
    <section className={`${CLASS_NAME}__cell`}>
      {left && (
        <>
          <span className={`${CLASS_NAME}__cell-label`}>{left.label}</span>
          <span className={`${CLASS_NAME}__cell-value`}>{left.value}</span>
        </>
      )}
    </section>
    {right && right.value !== undefined && (
      <section className={`${CLASS_NAME}__cell`}>
        <span className={`${CLASS_NAME}__cell-label`}>{right.label}</span>
        <span className={`${CLASS_NAME}__cell-value`}>{right.value}</span>
      </section>
    )}
  </section>
)

export const ReviewStep = ({
  formData,
  steps,
  onSubmit,
  onBack,
  onEditStep,
  isSubmitting,
  error,
}: ReviewStepProps) => {
  const claimant = (formData.claimant ?? {}) as Record<string, unknown>
  const contact = (formData.contact ?? {}) as Record<string, unknown>
  const tickets = (formData.tickets ?? []) as Record<string, unknown>[]
  const paymentMethod = formData.paymentMethod as string

  const errorRef = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [error])

  const findStepIndex = (key: string): number => {
    return steps.findIndex((s) => s.schemaKeys.includes(key))
  }

  const dobDisplay = formatIsoDateMMDDYYYY(claimant.dateOfBirth as string)

  return (
    <section className={CLASS_NAME}>
      <h2 className={`${CLASS_NAME}__title`}>Review & Submit</h2>
      <p className={`${CLASS_NAME}__subtitle`}>
        Review the information below to ensure its accuracy.
      </p>

      {/* Claimant Information */}
      <section className={`${CLASS_NAME}__section-header`}>
        <h3>Claimant Information</h3>
        <button type="button" onClick={() => onEditStep(findStepIndex('claimant'))}>
          <EditIcon />
        </button>
      </section>
      <section className={`${CLASS_NAME}__section`}>
        <section className={`${CLASS_NAME}__fields`}>
          <ReviewRow
            left={{ label: 'US Citizen?', value: claimant.usCitizen ? 'Yes' : 'No' }}
            right={{
              label: 'Original Claimant?',
              value: (claimant.originalClaimant as boolean) ? 'Yes' : 'No',
            }}
          />
          {claimant.originalClaimant === false &&
            (claimant.notOriginalPurchaserExplanation as string) && (
              <ReviewRow
                right={{
                  label: 'If not, explain',
                  value: claimant.notOriginalPurchaserExplanation as string,
                }}
              />
            )}
          <ReviewRow
            left={{ label: 'First Name', value: claimant.firstName as string }}
            right={{ label: 'Last Name', value: claimant.lastName as string }}
          />
          <ReviewRow
            left={{
              label: 'Middle Initial',
              value: (claimant.middleInitial as string) || undefined,
            }}
            right={{ label: 'Suffix', value: (claimant.suffix as string) || undefined }}
          />
          {claimant.usCitizen !== false ? (
            <ReviewRow
              left={{
                label: 'Social Security Number',
                value: maskSSN(claimant.socialSecurityNumber as string),
              }}
              right={{ label: 'Date of Birth', value: dobDisplay }}
            />
          ) : (
            <ReviewRow left={{ label: 'Date of Birth', value: dobDisplay }} />
          )}
        </section>
      </section>

      {/* Contact Information */}
      <section className={`${CLASS_NAME}__section-header`}>
        <h3>Contact Information</h3>
        <button type="button" onClick={() => onEditStep(findStepIndex('contact'))}>
          <EditIcon />
        </button>
      </section>
      <section className={`${CLASS_NAME}__section`}>
        <section className={`${CLASS_NAME}__fields`}>
          <ReviewRow
            left={{ label: 'Mailing Address 1', value: contact.mailingAddress1 as string }}
            right={{
              label: 'Mailing Address 2',
              value: (contact.mailingAddress2 as string) || undefined,
            }}
          />
          <ReviewRow
            left={{ label: 'City', value: contact.city as string }}
            right={{
              label: claimant.usCitizen !== false ? 'State' : 'Province/Region',
              value: contact.region as string,
            }}
          />
          <ReviewRow left={{ label: 'Postal Code', value: contact.postalCode as string }} />
          <ReviewRow
            left={{ label: 'Mobile Phone Number', value: contact.mobilePhoneNumber as string }}
            right={{
              label: 'Secondary Contact Number',
              value: (contact.secondaryContactNumber as string) || undefined,
            }}
          />
          <ReviewRow
            left={{
              label: 'Email Address',
              value: (contact.emailAddress as string) || undefined,
            }}
          />
        </section>
      </section>

      {/* Ticket Details */}
      <section className={`${CLASS_NAME}__section-header`}>
        <h3>Ticket Details</h3>
        <button type="button" onClick={() => onEditStep(findStepIndex('tickets'))}>
          <EditIcon />
        </button>
      </section>
      <section className={`${CLASS_NAME}__section`}>
        <section className={`${CLASS_NAME}__fields`}>
          {tickets.map((ticket, i) => (
            <section key={i} className={`${CLASS_NAME}__ticket-block`}>
              {tickets.length > 0 && (
                <p className={`${CLASS_NAME}__ticket-sub-header`}>
                  Ticket {i + 1} of {tickets.length}
                </p>
              )}
              <ReviewRow
                left={{
                  label: 'Prize Amount',
                  value: `$${(ticket.prizeAmount as number).toLocaleString()}`,
                }}
                right={{ label: 'Game Type', value: ticket.gameType as string }}
              />
              <ReviewRow
                left={{ label: 'Barcode Number', value: ticket.barcodeNumber as string }}
              />
            </section>
          ))}
          <section className={`${CLASS_NAME}__payment-method`}>
            <ReviewRow left={{ label: 'Payment Method', value: paymentMethod }} />
          </section>
        </section>
      </section>

      <FormFloatingFooter>
        {error && (
          <p ref={errorRef} className={`${CLASS_NAME}__error`} role="alert">
            {error}
          </p>
        )}
        <section className="floating-footer__buttons">
          <Button
            className="floating-footer__next-button"
            disabled={isSubmitting}
            onClick={onSubmit}>
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
          <Button className="floating-footer__previous-button" onClick={onBack}>
            {' '}
            <img src={PreviousChevron} alt="previous-icon" />
          </Button>
        </section>
      </FormFloatingFooter>
    </section>
  )
}
